import type { FC } from "react"
import { useState } from "react"

import { AxisBottom, AxisLeft } from "@visx/axis"
import { GridRows } from "@visx/grid"
import { Group } from "@visx/group"
import { ParentSize } from "@visx/responsive"
import { scaleLinear } from "@visx/scale"
import { LinePath } from "@visx/shape"

import type {
  ConsumptionInsightsSeason,
  TypicalDayViewDto,
} from "~/api/dto/proposal-consumption.dto"

import {
  AXIS_COLOR,
  EVENING_BAND_FILL,
  EVENING_BAND_LABEL,
  EVENING_WINDOW,
  GRID_COLOR,
  SEASON_COLORS,
  SEASON_LABELS,
  SEASONS,
  axisLabelProps,
  formatHour,
  formatKw,
} from "./chart-theme"

const MARGIN = { top: 16, right: 76, bottom: 32, left: 44 }

const availableSeasons = (
  viewModel: TypicalDayViewDto,
): ConsumptionInsightsSeason[] =>
  SEASONS.filter((season) => {
    const series = viewModel.seasons[season]
    return Array.isArray(series) && series.length === 24
  })

type ChartProps = {
  height: number
  viewModel: TypicalDayViewDto
  width: number
}

const Chart: FC<ChartProps> = ({ height, viewModel, width }) => {
  const [hoverHour, setHoverHour] = useState<number | null>(null)
  const seasons = availableSeasons(viewModel)
  const innerWidth = width - MARGIN.left - MARGIN.right
  const innerHeight = height - MARGIN.top - MARGIN.bottom
  if (innerWidth <= 0 || innerHeight <= 0) return null
  if (seasons.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted">
        No seasons have enough observed days to chart yet.
      </div>
    )
  }

  const maxKw = Math.max(
    ...seasons.flatMap((season) => viewModel.seasons[season] ?? []),
  )
  const xScale = scaleLinear({ domain: [0, 23], range: [0, innerWidth] })
  const yScale = scaleLinear({
    domain: [0, Math.max(maxKw * 1.1, 0.1)],
    nice: true,
    range: [innerHeight, 0],
  })

  return (
    <div className="relative">
      <svg
        height={height}
        role="img"
        aria-label={`Average ${viewModel.measurementBasis === `GRID_IMPORT` ? `grid import` : `household load`} by hour of day, per season`}
        width={width}
        onMouseLeave={() => setHoverHour(null)}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect()
          const x = event.clientX - rect.left - MARGIN.left
          setHoverHour(Math.max(0, Math.min(23, Math.round(xScale.invert(x)))))
        }}
      >
        <Group left={MARGIN.left} top={MARGIN.top}>
          <rect
            fill={EVENING_BAND_FILL}
            fillOpacity={0.12}
            height={innerHeight}
            width={
              xScale(EVENING_WINDOW.endHourExclusive) -
              xScale(EVENING_WINDOW.startHour)
            }
            x={xScale(EVENING_WINDOW.startHour)}
            y={0}
          />
          <text
            fill={EVENING_BAND_LABEL}
            fontFamily="Barlow, sans-serif"
            fontSize={10}
            fontWeight={600}
            x={xScale(EVENING_WINDOW.startHour) + 4}
            y={12}
          >
            Evening window
          </text>
          <GridRows
            height={innerHeight}
            scale={yScale}
            stroke={GRID_COLOR}
            width={innerWidth}
          />
          {seasons.map((season) => (
            <LinePath<number>
              key={season}
              data={viewModel.seasons[season] ?? []}
              stroke={SEASON_COLORS[season]}
              strokeWidth={2}
              x={(_, hour) => xScale(hour)}
              y={(value) => yScale(value)}
            />
          ))}
          {(() => {
            const MIN_GAP = 14
            const placed = seasons
              .map((season) => ({
                season,
                y: yScale((viewModel.seasons[season] ?? [])[23] ?? 0) + 4,
              }))
              .toSorted((a, b) => a.y - b.y)
            for (let index = 1; index < placed.length; index += 1) {
              if (placed[index].y - placed[index - 1].y < MIN_GAP) {
                placed[index].y = placed[index - 1].y + MIN_GAP
              }
            }
            return placed.map(({ season, y }) => (
              <text
                key={`${season}-label`}
                fill={SEASON_COLORS[season]}
                fontFamily="Barlow, sans-serif"
                fontSize={11}
                fontWeight={700}
                x={innerWidth + 6}
                y={y}
              >
                {SEASON_LABELS[season]}
              </text>
            ))
          })()}
          {hoverHour !== null ? (
            <g>
              <line
                stroke={AXIS_COLOR}
                strokeDasharray="3,3"
                x1={xScale(hoverHour)}
                x2={xScale(hoverHour)}
                y1={0}
                y2={innerHeight}
              />
              {seasons.map((season) => (
                <circle
                  key={`${season}-dot`}
                  cx={xScale(hoverHour)}
                  cy={yScale((viewModel.seasons[season] ?? [])[hoverHour] ?? 0)}
                  fill={SEASON_COLORS[season]}
                  r={4}
                  stroke="#ffffff"
                  strokeWidth={2}
                />
              ))}
            </g>
          ) : null}
          <AxisBottom
            scale={xScale}
            stroke={AXIS_COLOR}
            tickFormat={(value) => formatHour(Number(value))}
            tickLabelProps={() => ({ ...axisLabelProps, textAnchor: `middle` })}
            tickStroke={AXIS_COLOR}
            tickValues={[0, 4, 8, 12, 16, 20, 23]}
            top={innerHeight}
          />
          <AxisLeft
            scale={yScale}
            stroke={AXIS_COLOR}
            tickFormat={(value) => `${Number(value)} kW`}
            tickLabelProps={() => ({
              ...axisLabelProps,
              dx: `-0.25em`,
              dy: `0.25em`,
              textAnchor: `end`,
            })}
            tickStroke={AXIS_COLOR}
          />
        </Group>
      </svg>
      {hoverHour !== null ? (
        <div className="pointer-events-none absolute top-2 left-14 rounded-lg border border-border bg-background px-3 py-2 shadow-md">
          <p className="font-display text-xs font-bold text-foreground">
            {formatHour(hoverHour)}
          </p>
          {seasons.map((season: ConsumptionInsightsSeason) => (
            <p
              key={season}
              className="mt-0.5 flex items-center gap-1.5 text-xs text-muted"
            >
              <span
                className="inline-block size-2 rounded-full"
                style={{ backgroundColor: SEASON_COLORS[season] }}
              />
              {SEASON_LABELS[season]}:{` `}
              <span className="font-semibold tabular-nums text-foreground">
                {formatKw((viewModel.seasons[season] ?? [])[hoverHour] ?? 0)}
              </span>
            </p>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export const LoadShapeChart: FC<{ viewModel: TypicalDayViewDto }> = ({
  viewModel,
}) => {
  const seasons = availableSeasons(viewModel)
  return (
    <div>
      <div className="flex flex-wrap items-center gap-4">
        {seasons.map((season) => (
          <span
            key={season}
            className="flex items-center gap-1.5 text-xs font-semibold text-muted"
          >
            <span
              className="inline-block h-0.5 w-4 rounded-full"
              style={{ backgroundColor: SEASON_COLORS[season] }}
            />
            {SEASON_LABELS[season]}
          </span>
        ))}
      </div>
      <div className="mt-2 h-[300px]">
        <ParentSize>
          {({ height, width }) => (
            <Chart height={height} viewModel={viewModel} width={width} />
          )}
        </ParentSize>
      </div>
    </div>
  )
}
